import React, { Fragment, useState, useRef, useEffect } from "react";
import PageTitle from "../../layouts/PageTitle";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../firebase";
import {
  getDoc,
  getDocs,
  collection,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  PatientTextInputForm,
  PatientTextInputNotRequiredForm,
  PatientDropDownForm,
  PatientDropDownFormRequired,
  PatientDateOfBirthDropdown,
} from "./PatientUtils";
import {
  notifyTopRight,
  notifyWarning,
  notifyError,
} from "../../components/toast/SetToast";
import Toast from "../../components/toast/SetToast";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import {
  getPatientImportDateOfBirthFromRow,
  getPatientImportRows,
  getHHHHealthInsuranceFromRow,
  isHHHNode,
} from "./patientImportUtils";
import { filterCustomersForStaffNode } from "../../../utils/staffCustomerAccess";
import { normalizePatientHn } from "../../../utils/patientPageUtils";

const PatientRegister = () => {
  const profileData = {
    hn: "",
    email: "",
    titleTh: "",
    firstnameTh: "",
    lastnameTh: "",
    nationalId: "",
    titleEn: "",
    firstnameEn: "",
    lastnameEn: "",
    dateOfBirth: "",
    status: "",
    ethnicity: "",
    nationality: "",
    religion: "",
    bloodType: "",
    note: "",
    gender: "",
    occupation: "",
    mobile: "",
    telephone: "",
    address1: "",
    address2: "",
    province: "",
    postcode: "",
    medicineIntolerance: "",
    healthInsurance: "",
    other: "",
    emergencyContact: "",
    relationship: "",
    emergencyContactPhone: "",
    refHn: "",
    refCustomer: "",
    nodeRef: "",
    stopBangScore: "",
    comorbidity: "",
  };

  const [comorbidityList, setComorbidityList] = useState([]);
  const [comorbidityOtherChecked, setComorbidityOtherChecked] = useState(false);
  const [comorbidityOtherText, setComorbidityOtherText] = useState("");
  const profileRef = useRef(profileData);

  const [nodeList, setNodeList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [customerAllList, setCustomerAllList] = useState([]);
  const [hn, setHN] = useState("");

  const [isImporting, setIsImporting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [importNodeName, setImportNodeName] = useState("");
  const [importSheetMode, setImportSheetMode] = useState("single");
  const [importSheetName, setImportSheetName] = useState("");

  const { currentUser } = useAuth();

  useEffect(() => {
    const parts = [].concat(comorbidityList);
    if (comorbidityOtherChecked && comorbidityOtherText.trim()) {
      parts.push(comorbidityOtherText.trim());
    }
    if (profileRef.current["comorbidity"]) {
      profileRef.current["comorbidity"].value = parts.join(", ");
    }
  }, [comorbidityList, comorbidityOtherChecked, comorbidityOtherText]);

  const marriedStatus = ["โสด", "สมรส", "หย่า", "แยกกันอยู่", "หม้าย", "สมณะ"];
  const genders = ["ชาย", "หญิง"];
  const paymentPrivilages = [
    "ชำระเงินเอง",
    "ประกันสุขภาพถ้วนหน้า",
    "ประกันสังคม",
    "กรมบัญชีกลาง",
    "กรุงเทพมหานคร",
    "ต้นสังกัด",
    "นักศึกษา มธ.",
    "โครงการวิจัยรับค่าบริการล่วงหน้า",
    "องค์การปกครองส่วนท้องถิ่น",
    "โครงการตรวจสุขภาพรับค่าบริการล่วงหน้า",
    "ผ่อนชำระ/นิติกร",
    "อุบัติเหตุและฉุกเฉินนอกโรงพยาบาล(EMS)",
    "ปลัดกระทรวงสาธารณะสุข",
    "โครงการ 85 ปี",
    "อื่นๆ",
  ];

  const hospitalMap = {
    CHG09: "CH 9 Airport Hospital",
    CHG01: "Chularat 1 Suvarnabhumi Hospital",
    CHG11: "Chularat 11 International Hospital",
    CHG02: "Chularat 2 Medical Clinic Theparak",
    CHG03: "Chularat 3 International Hospital",
    CHG304: "Chularat 304 international hospital",
    CHG05: "Chularat 5 Hospital",
    CHG07: "Chularat 7 PRAVEJ Clinic",
    CHG08: "Chularat 8 LADKRABANG Clinic",
    CHGCHOL: "Chularat Cholvaej Hospital",
    CHYRY: "Chularat Rayong Hospital",
    CENTURY: "Century Care Center",
  };

  const CASH_NODE_NAMES = [
    "Anya Meditec",
    "Samitivej Thonburi Hospital",
    "BNH Hospital",
    "Pat Rangsit Mother and Child Hospital",
    "Ekachai Hospital",
    "Brain Dynamic Technology",
  ];

  const SOCIAL_SECURITY_NODE_NAMES = [
    "Bangkok Hospital Pakchong",
    "Bangkok Hospital Pluakdaeng",
    "Bangkok Hospital Ratchasima",
    "Bangmod Hospital",
    "Chularat Hospital Group",
    "Ladprao Hospital",
    "Pat Rangsit Hospital Group",
  ];

  const mapHospitalToCustomer = (value) => {
    const raw = String(value || "").trim();
    const upper = raw.toUpperCase();

    if (hospitalMap[upper]) return hospitalMap[upper];
    if (upper === "CENTURY" || upper === "BDSLEEP") {
      return "Century Care Center";
    }
    if (!upper || upper.indexOf("CHG") !== 0) {
      return "Century Care Center";
    }

    return "Century Care Center";
  };

  const normalizeGenderToThai = (value) => {
    const raw = String(value || "")
      .trim()
      .toLowerCase();
    if (!raw) return "";
    if (raw === "male" || raw === "m" || raw === "ชาย") return "ชาย";
    if (raw === "female" || raw === "f" || raw === "หญิง") return "หญิง";
    return "";
  };

  const normalizeText = (value) => String(value != null ? value : "").trim();

  const normalizeSearchText = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const hasText = (value, keyword) => {
    return (
      normalizeSearchText(value).indexOf(normalizeSearchText(keyword)) !== -1
    );
  };

  const getDynamicHnColumn = (row) => {
    const keys = Object.keys(row || {});
    for (let i = 0; i < keys.length; i++) {
      const key = normalizeText(keys[i]);
      if (key && key !== "HN" && key.toUpperCase().indexOf("HN ") === 0) {
        return key;
      }
    }
    return "";
  };

  const getCombinedNote = (row) => {
    const note1 = normalizeText(row["Note"]);
    const note2 = normalizeText(row["หมายเหตุ"]);
    if (note1 && note2) return `${note1} | ${note2}`;
    return note1 || note2 || "";
  };

  const getAddress1Value = (row) => {
    return normalizeText(row["ที่อยู่"]) || normalizeText(row["Address"]);
  };

  const getHealthInsuranceByNode = (selectedNode, row) => {
    // console.log("selected node name for health insurance: ", selectedNode.nodename);
    if (isHHHNode(selectedNode)) {
      return getHHHHealthInsuranceFromRow(row);
    }
    if (normalizeText(selectedNode.nodecode).toUpperCase() === "SGH") {
      return hasText(row["สิทธิ์ที่ใช้"], "เงินสด")
        ? "ชำระเงินเอง"
        : "ประกันสังคม";
    }

    if (CASH_NODE_NAMES.indexOf(selectedNode.nodename) !== -1) {
      return "ชำระเงินเอง";
    }

    if (SOCIAL_SECURITY_NODE_NAMES.indexOf(selectedNode.nodename) !== -1) {
      if (selectedNode.nodename === "Ladprao Hospital") {
        const remark = normalizeText(row["หมายเหตุ"]);
        if (hasText(remark, "เงินสด")) {
          return "ชำระเงินเอง";
        }
        return "ประกันสังคม";
      }

      if (selectedNode.nodename === "Chularat Hospital Group") {
        if (normalizeText(row["HN"]) === "HN-69020227") {
          console.log("debugging insurance for HN-69020227: ", row);
          console.log(
            "debugging insurance for HN-69020227 package: ",
            row["แพ็กเกจการตรวจ"],
          );
          console.log(
            "debugging insurance for HN-69020227 right: ",
            hasText(normalizeText(row["แพ็กเกจการตรวจ"]), "เงินสด"),
          );
          console.log(mapHospitalToCustomer(row["Hospital"]));
        }
        const packageText = normalizeText(row["แพ็กเกจการตรวจ"]);
        if (hasText(packageText, "เงินสด")) {
          return "ชำระเงินเอง";
        }
        if (hasText(packageText, "ตามสิทธิ")) {
          return "ประกันสังคม";
        }
        return "ประกันสังคม";
      }

      const rightText = normalizeText(row["สิทธิ์ที่ใช้"]);
      if (hasText(rightText, "เงินสด")) {
        return "ชำระเงินเอง";
      }
      if (hasText(rightText, "ประกันสังคม") || hasText(rightText, "ตามสิทธิ")) {
        return "ประกันสังคม";
      }
      return "ประกันสังคม";
    }

    return "";
  };

  const getPatientNameKey = (firstname, lastname) => {
    return normalizeSearchText(firstname) + "|" + normalizeSearchText(lastname);
  };

  const makeProblemRow = (
    type,
    rowNumber,
    selectedNode,
    excelPatient,
    dbPatient,
  ) => {
    return {
      Type: type,
      Row: rowNumber,
      Node: normalizeText(selectedNode.nodename),
      NodeCode: normalizeText(selectedNode.nodecode),

      Excel_HN: normalizeText(excelPatient.hn),
      Excel_FirstnameTh: normalizeText(excelPatient.firstnameTh),
      Excel_LastnameTh: normalizeText(excelPatient.lastnameTh),

      DB_HN: normalizeText(dbPatient && (dbPatient.hn || dbPatient.id)),
      DB_FirstnameTh: normalizeText(dbPatient && dbPatient.firstnameTh),
      DB_LastnameTh: normalizeText(dbPatient && dbPatient.lastnameTh),
    };
  };

  const makeMissingRequiredRow = (
    rowNumber,
    selectedNode,
    excelPatient,
    missingFields,
  ) => {
    return {
      Row: rowNumber,
      Node: normalizeText(selectedNode.nodename),
      NodeCode: normalizeText(selectedNode.nodecode),
      MissingFields: missingFields.join(", "),
      Excel_HN: normalizeText(excelPatient.hn),
      Excel_FirstnameTh: normalizeText(excelPatient.firstnameTh),
      Excel_LastnameTh: normalizeText(excelPatient.lastnameTh),
    };
  };

  const downloadProblemWorkbook = (
    hnMismatchProblems,
    nameMismatchProblems,
    missingRequiredProblems,
  ) => {
    if (
      !hnMismatchProblems.length &&
      !nameMismatchProblems.length &&
      !missingRequiredProblems.length
    ) {
      return;
    }

    const wb = XLSX.utils.book_new();

    const hnData = hnMismatchProblems.length
      ? hnMismatchProblems
      : [{ Note: "No HN mismatch problems" }];

    const nameData = nameMismatchProblems.length
      ? nameMismatchProblems
      : [{ Note: "No patient name mismatch problems" }];

    const missingData = missingRequiredProblems.length
      ? missingRequiredProblems
      : [{ Note: "No missing required data problems" }];

    const wsHn = XLSX.utils.json_to_sheet(hnData);
    const wsName = XLSX.utils.json_to_sheet(nameData);
    const wsMissing = XLSX.utils.json_to_sheet(missingData);

    XLSX.utils.book_append_sheet(wb, wsHn, "HN Mismatch");
    XLSX.utils.book_append_sheet(wb, wsName, "Patient Name Mismatch");
    XLSX.utils.book_append_sheet(wb, wsMissing, "Missing Required Data");

    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");

    const filename =
      "patient_import_problems_" +
      yyyy +
      mm +
      dd +
      "_" +
      hh +
      mi +
      ss +
      ".xlsx";

    XLSX.writeFile(wb, filename);
  };

  const submitHandler = async (e) => {
    try {
      e.preventDefault();
      const patientHn = normalizePatientHn(hn);

      if (!patientHn) {
        notifyError("HN is required");
        return;
      }

      setHN(patientHn);
      if (
        profileRef.current["hn"] &&
        profileRef.current["hn"].value !== undefined
      ) {
        profileRef.current["hn"].value = patientHn;
      }

      const ref = doc(db, "patients", patientHn);
      const docSnap = await getDoc(ref);

      if (docSnap.exists()) {
        notifyError("Invalid HN !");
      } else {
        const patientData = {
          hn: patientHn,
          email: profileRef.current["email"].value.trim(),
          titleTh: profileRef.current["titleTh"].value.trim(),
          firstnameTh: profileRef.current["firstnameTh"].value.trim(),
          lastnameTh: profileRef.current["lastnameTh"].value.trim(),
          nationalId: profileRef.current["nationalId"].value.trim(),
          titleEn: profileRef.current["titleEn"].value,
          firstnameEn: profileRef.current["firstnameEn"].value,
          lastnameEn: profileRef.current["lastnameEn"].value,
          dateOfBirth: profileRef.current["dateOfBirth"].value,
          status: profileRef.current["status"].value,
          ethnicity: profileRef.current["ethnicity"].value,
          nationality: profileRef.current["nationality"].value,
          religion: profileRef.current["religion"].value,
          bloodType: profileRef.current["bloodType"].value,
          note: profileRef.current["note"].value,
          gender: profileRef.current["gender"].value,
          occupation: profileRef.current["occupation"].value,
          mobile: profileRef.current["mobile"].value.trim(),
          telephone: profileRef.current["telephone"].value.trim(),
          address1: profileRef.current["address1"].value,
          address2: profileRef.current["address2"].value,
          province: profileRef.current["province"].value,
          postcode: profileRef.current["postcode"].value,
          medicineIntolerance: profileRef.current["medicineIntolerance"].value,
          healthInsurance: profileRef.current["healthInsurance"].value,
          other: profileRef.current["other"].value,
          emergencyContact: profileRef.current["emergencyContact"].value,
          relationship: profileRef.current["relationship"].value,
          emergencyContactPhone:
            profileRef.current["emergencyContactPhone"].value,
          refHn: profileRef.current["refHn"].value.trim(),
          refCustomer: profileRef.current["refCustomer"].value,
          nodeRef: profileRef.current["nodeRef"].value,
          createdAt: serverTimestamp(),
          loginAccount: null,
          stopBangScore: profileRef.current["stopBangScore"].value,
          comorbidity: profileRef.current["comorbidity"].value,
        };

        await setDoc(ref, patientData);
        notifyTopRight("Create success ");

        for (const key in profileRef.current) {
          if (
            Object.prototype.hasOwnProperty.call(profileRef.current, key) &&
            key !== "nodeRef" &&
            key !== "refCustomer"
          ) {
            if (
              profileRef.current[key] &&
              profileRef.current[key].value !== undefined
            ) {
              profileRef.current[key].value = "";
            }
          }
        }

        setComorbidityList([]);
        setComorbidityOtherChecked(false);
        setComorbidityOtherText("");
        setHN("");

        document.querySelectorAll('[id^="comorbidity-"]').forEach((el) => {
          el.checked = false;
        });
      }
    } catch (err) {
      console.log(err);
      notifyError(`Error ${err}`);
    }
  };

  const CustomerList = (nodename) => {
    const allNodes = nodeList.map((node) => node.nodename);
    const index = allNodes.indexOf(nodename);
    return customerAllList[index] || [];
  };

  useEffect(() => {
    const loadNodes = async () => {
      try {
        const nodesDocs = await getDocs(collection(db, "nodes"));

        let allCustomers = [];
        let allNodes = [];

        if (currentUser.roles[0] === "super_admin") {
          allNodes = nodesDocs.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          }));

          for (const nodeDoc of nodesDocs.docs) {
            const customerDocs = await getDocs(
              collection(db, "nodes", nodeDoc.id, "customers"),
            );
            const customersData = customerDocs.docs.map(
              (customer) => customer.id,
            );
            allCustomers.push(customersData);
          }
        } else {
          const currentAdmin = await getDoc(
            doc(db, "admins", currentUser.displayName),
          );
          const currentAdminData = currentAdmin.exists()
            ? currentAdmin.data()
            : {};
          let nodeCode = currentAdminData.node || [];

          if (!Array.isArray(nodeCode)) {
            nodeCode = nodeCode ? [nodeCode] : [];
          }

          const node = nodesDocs.docs.filter((item) =>
            nodeCode.includes(item.data().nodecode),
          );

          allNodes = node.map((item) => ({
            id: item.id,
            ...item.data(),
          }));

          for (let i = 0; i < node.length; i++) {
            const customerDocs = await getDocs(
              collection(db, "nodes", node[i].id, "customers"),
            );
            const allowedCustomers = filterCustomersForStaffNode({
              customers: customerDocs.docs,
              currentUser,
              adminData: currentAdminData,
              nodeCode: node[i].data().nodecode,
              getCustomerId: (customerDoc) => customerDoc.id,
            });
            const customersData = allowedCustomers.map(
              (customer) => customer.id,
            );
            allCustomers.push(customersData);
          }
        }

        setCustomerAllList(allCustomers);
        setCustomerList(allCustomers[0] || []);
        setNodeList(allNodes);
      } catch (e) {
        console.log(e);
        notifyError(`Load node error: ${e}`);
      }
    };

    loadNodes();
  }, [currentUser]);

  const onChangeNode = async (nodename) => {
    if (nodename === "") {
      profileRef.current["nodeRef"] = "";
      return;
    }

    profileRef.current["nodeRef"] = nodename;
  };

  const handleConfirmImport = async () => {
    if (!pendingFile) {
      notifyWarning("กรุณาเลือกไฟล์ก่อน");
      return;
    }

    if (!importNodeName) {
      notifyWarning("กรุณาเลือก Node สำหรับ batch นี้");
      return;
    }

    if (importSheetMode === "multi" && !normalizeText(importSheetName)) {
      await Swal.fire({
        icon: "error",
        title: "Sheet name is required",
        text: "Please enter the sheet name before importing a multiple-sheet file.",
        confirmButtonText: "OK",
      });
      return;
    }

    const selectedNode = nodeList.find(
      (node) => node.nodename === importNodeName,
    );

    if (!selectedNode) {
      notifyError("ไม่พบ Node ที่เลือก");
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Confirm import",
      html:
        `<div style="text-align:left">` +
        `<div><b>Node:</b> ${selectedNode.nodename || "-"}</div>` +
        `<div><b>Node Code:</b> ${selectedNode.nodecode || "-"}</div>` +
        `<div><b>Filename:</b> ${pendingFile.name || "-"}</div>` +
        `</div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit Import",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    await importFromXlsx(
      pendingFile,
      importNodeName,
      importSheetMode,
      importSheetName,
    );

    setPendingFile(null);

    const input = document.getElementById("xlsx-file-input");
    if (input) input.value = "";
  };

  const importFromXlsx = async (
    file,
    selectedNodeName,
    sheetMode,
    selectedSheetName,
  ) => {
    setIsImporting(true);

    try {
      if (!file) return;

      const selectedNode = nodeList.find(
        (node) => node.nodename === selectedNodeName,
      );

      if (!selectedNode) {
        notifyError("ไม่พบ Node ที่เลือก");
        return;
      }

      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, {
        type: "array",
        cellDates: false,
        raw: false,
      });

      let sheetNameToRead = wb.SheetNames[0];

      if (sheetMode === "multi") {
        const inputSheetName = normalizeText(selectedSheetName);

        if (!inputSheetName) {
          await Swal.fire({
            icon: "error",
            title: "Sheet name is required",
            text: "Please enter the sheet name before importing a multiple-sheet file.",
            confirmButtonText: "OK",
          });
          return;
        }

        if (!wb.Sheets[inputSheetName]) {
          await Swal.fire({
            icon: "error",
            title: "Sheet not found",
            html:
              `<div style="text-align:left">` +
              `<div><b>Sheet name:</b> ${inputSheetName}</div>` +
              `<div><b>Available sheets:</b> ${wb.SheetNames.join(", ")}</div>` +
              `</div>`,
            confirmButtonText: "OK",
          });
          return;
        }

        sheetNameToRead = inputSheetName;
      }

      const ws = wb.Sheets[sheetNameToRead];
      if (!ws) {
        notifyError("ไม่พบข้อมูลในไฟล์");
        return;
      }

      const rows = getPatientImportRows(ws);

      if (!rows.length) {
        notifyWarning("ไฟล์ว่าง");
        return;
      }

      const patientDocs = await getDocs(collection(db, "patients"));

      const existingByHn = new Map();
      const existingByName = new Map();

      patientDocs.forEach((item) => {
        const data = item.data() || {};
        const record = {
          id: item.id,
          ...data,
          hn: normalizePatientHn(data.hn || item.id),
        };

        const hnKey = normalizePatientHn(record.hn);
        if (hnKey) {
          existingByHn.set(hnKey, record);
        }

        const nameKey = getPatientNameKey(
          record.firstnameTh,
          record.lastnameTh,
        );

        if (
          normalizeText(record.firstnameTh) &&
          normalizeText(record.lastnameTh)
        ) {
          if (!existingByName.has(nameKey)) {
            existingByName.set(nameKey, []);
          }
          existingByName.get(nameKey).push(record);
        }
      });

      let created = 0;
      let updated = 0;
      const errors = [];
      const hnMismatchProblems = [];
      const patientNameMismatchProblems = [];
      const missingRequiredProblems = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] || {};
        const rnum = i + 2;

        const nodeCode = normalizeText(selectedNode.nodecode).toUpperCase();
        const refHnKeyFromNode = nodeCode ? `HN ${nodeCode}` : "";
        const dynamicHnKey = getDynamicHnColumn(row);

        const hnFromHN = normalizePatientHn(row["HN"]);
        const dynamicHnValue = dynamicHnKey
          ? normalizePatientHn(row[dynamicHnKey])
          : "";

        const hnValue = hnFromHN || dynamicHnValue;

        let refHnValue = "";
        if (refHnKeyFromNode) {
          refHnValue = normalizeText(row[refHnKeyFromNode]);
        }
        if (!hnFromHN && dynamicHnValue) {
          refHnValue = dynamicHnValue;
        }

        const firstnameTh = normalizeText(row["ชื่อ"]);
        const lastnameTh = normalizeText(row["นามสกุล"]);
        const titleTh = normalizeText(row["คำนำหน้า"]);
        const refCustomer =
          normalizeText(selectedNode.nodename) === "Chularat Hospital Group"
            ? mapHospitalToCustomer(row["Hospital"])
            : normalizeText(selectedNodeName);
        const address1Value = getAddress1Value(row);
        const noteValue = getCombinedNote(row);
        const healthInsuranceValue = getHealthInsuranceByNode(
          selectedNode,
          row,
        );

        const rawDateOfBirth = normalizeText(row["DOB"]);
        const dateOfBirth = getPatientImportDateOfBirthFromRow(
          row,
          !!(wb.Workbook && wb.Workbook.WBProps && wb.Workbook.WBProps.date1904),
        );
        const dobInvalid = rawDateOfBirth && !dateOfBirth;
        const stopBangScore = normalizeText(row["Stop bang"]);

        const patientData = {
          hn: hnValue,
          email: normalizeText(row["E-mail"]),
          titleTh: titleTh,
          firstnameTh: firstnameTh,
          lastnameTh: lastnameTh,
          nationalId: normalizeText(row["Personal ID"]),
          titleEn: "",
          firstnameEn: "",
          lastnameEn: "",
          dateOfBirth: dateOfBirth,
          status: "",
          ethnicity: "",
          nationality: "",
          religion: "",
          bloodType: "",
          note: noteValue,
          gender: normalizeGenderToThai(row["Gender"]),
          occupation: "",
          mobile: normalizeText(row["Phone No."]),
          telephone: "",
          address1: address1Value,
          address2: "",
          province: "",
          postcode: "",
          medicineIntolerance: "",
          healthInsurance: healthInsuranceValue,
          other: "",
          emergencyContact: "",
          relationship: "",
          emergencyContactPhone: "",
          refHn: refHnValue,
          refCustomer: refCustomer,
          nodeRef: selectedNodeName,
          loginAccount: null,
          stopBangScore: stopBangScore,
          comorbidity: "",
        };

        const missingFields = [];
        if (!patientData.hn) missingFields.push("hn");
        if (!patientData.firstnameTh) missingFields.push("firstnameTh");
        if (!patientData.lastnameTh) missingFields.push("lastnameTh");
        if (dobInvalid) {
          missingFields.push("DOB invalid");
          errors.push(`แถว ${rnum}: DOB ไม่ถูกต้อง (${rawDateOfBirth})`);
        }

        if (missingFields.length > 0) {
          missingRequiredProblems.push(
            makeMissingRequiredRow(
              rnum,
              selectedNode,
              patientData,
              missingFields,
            ),
          );
          continue;
        }

        const excelNameKey = getPatientNameKey(
          patientData.firstnameTh,
          patientData.lastnameTh,
        );

        const existingPatientByHn = existingByHn.get(patientData.hn);

        if (existingPatientByHn) {
          const dbNameKey = getPatientNameKey(
            existingPatientByHn.firstnameTh,
            existingPatientByHn.lastnameTh,
          );

          if (dbNameKey === excelNameKey) {
            try {
              const updatePayload = {
                ...patientData,
                updatedAt: serverTimestamp(),
              };

              await setDoc(doc(db, "patients", patientData.hn), updatePayload, {
                merge: true,
              });

              updated++;

              const updatedRecord = {
                ...existingPatientByHn,
                ...patientData,
              };
              existingByHn.set(patientData.hn, updatedRecord);
            } catch (e) {
              errors.push(
                `แถว ${rnum}: อัปเดต Firestore ล้มเหลว (${
                  e && e.message ? e.message : e
                })`,
              );
            }

            continue;
          }

          hnMismatchProblems.push(
            makeProblemRow(
              "Same HN but different firstname/lastname",
              rnum,
              selectedNode,
              patientData,
              existingPatientByHn,
            ),
          );
          continue;
        }

        const sameNamePatients = existingByName.get(excelNameKey) || [];
        if (sameNamePatients.length > 0) {
          const dbPatient = sameNamePatients[0];

          patientNameMismatchProblems.push(
            makeProblemRow(
              "Same firstname/lastname but different HN",
              rnum,
              selectedNode,
              patientData,
              dbPatient,
            ),
          );
          continue;
        }

        try {
          const createPayload = {
            ...patientData,
            createdAt: serverTimestamp(),
          };

          await setDoc(doc(db, "patients", patientData.hn), createPayload);

          created++;

          const newRecord = {
            ...patientData,
            id: patientData.hn,
          };

          existingByHn.set(patientData.hn, newRecord);

          if (!existingByName.has(excelNameKey)) {
            existingByName.set(excelNameKey, []);
          }
          existingByName.get(excelNameKey).push(newRecord);
        } catch (e) {
          errors.push(
            `แถว ${rnum}: บันทึก Firestore ล้มเหลว (${
              e && e.message ? e.message : e
            })`,
          );
        }
      }

      const totalProblems =
        hnMismatchProblems.length +
        patientNameMismatchProblems.length +
        missingRequiredProblems.length;

      if (totalProblems > 0) {
        downloadProblemWorkbook(
          hnMismatchProblems,
          patientNameMismatchProblems,
          missingRequiredProblems,
        );
        notifyWarning(
          `พบข้อมูลมีปัญหา ${totalProblems} แถว และดาวน์โหลดไฟล์ปัญหาแล้ว`,
        );
      }

      if (created || updated) {
        notifyTopRight(
          `นำเข้าเสร็จสิ้น: สร้าง ${created} ราย, อัปเดต ${updated} ราย`,
        );
      }

      if (errors.length) {
        notifyWarning(
          `${errors.length} แถวมีปัญหา:\n` +
            errors.slice(0, 10).join("\n") +
            (errors.length > 10 ? "\n..." : ""),
        );
      }
    } catch (e) {
      console.error(e);
      notifyError(`นำเข้าไม่สำเร็จ: ${e && e.message ? e.message : e}`);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Fragment>
      <PageTitle
        activeMenu="New Patient"
        motherMenu="Patient"
        motherLink="/patient-list"
      />
      <Toast />

      <div className="row">
        <div className="col-lg-12">
          <div
            className="batch-import-card"
            style={{
              border: "1px solid #e9ecef",
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              background: "#fff",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 12,
                color: "#1f1f1f",
              }}
            >
              Batch Import Patient by File
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "flex-end",
              }}
            >
              <div style={{ minWidth: 320, maxWidth: 640, flex: "1 1 320px" }}>
                <PatientDropDownFormRequired
                  title="Node"
                  id="import-node"
                  placeholder="กดเพื่อเลือก"
                  options={nodeList.map(
                    (node) => `${node.nodename} (${node.nodecode || "-"})`,
                  )}
                  onChange={(e) => {
                    const selectedLabel = e.target.value;
                    const selectedNode = nodeList.find(
                      (node) =>
                        `${node.nodename} (${node.nodecode || "-"})` ===
                        selectedLabel,
                    );
                    setImportNodeName(
                      selectedNode ? selectedNode.nodename : "",
                    );
                  }}
                  refvalue={() => {}}
                />
              </div>

              <div style={{ minWidth: 220 }}>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 6,
                    display: "block",
                    color: "#424242",
                  }}
                >
                  Sheet Type
                </label>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sheet-mode-single"
                    checked={importSheetMode === "single"}
                    onChange={() => {
                      setImportSheetMode("single");
                      setImportSheetName("");
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="sheet-mode-single"
                  >
                    Single sheet
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sheet-mode-multi"
                    checked={importSheetMode === "multi"}
                    onChange={() => setImportSheetMode("multi")}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="sheet-mode-multi"
                  >
                    Multiple sheets
                  </label>
                </div>
              </div>

              {importSheetMode === "multi" && (
                <div
                  style={{ minWidth: 240, maxWidth: 320, flex: "1 1 240px" }}
                >
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      marginBottom: 6,
                      display: "block",
                      color: "#424242",
                    }}
                  >
                    Sheet Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="กรอกชื่อชีท"
                    value={importSheetName}
                    onChange={(e) => setImportSheetName(e.target.value)}
                  />
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                <input
                  id="xlsx-file-input"
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) setPendingFile(f);
                  }}
                />

                <label
                  htmlFor="xlsx-file-input"
                  className="btn btn-outline-primary mb-0"
                >
                  Choose .xlsx
                </label>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmImport}
                  disabled={isImporting || !pendingFile || !importNodeName}
                >
                  {isImporting ? "Importing..." : "Submit Import"}
                </button>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <small className="text-muted d-block">
                ใช้ Node ที่เลือกกับทุกแถวในไฟล์ และข้าม HN
                ซ้ำหรือแถวที่ไม่มีชื่อ
              </small>
              <small className="text-muted d-block">
                Single sheet = อ่านชีทแรก, Multiple sheets =
                อ่านจากชื่อชีทที่กรอก
              </small>
              <small
                className="d-block mt-1"
                style={{ fontWeight: 700, color: "#dc3545" }}
              >
                ถ้าไม่มีคอลัมน์ HN ระบบจะใช้ HN {"<nodecode>"} แทน และใช้ HN{" "}
                {"<nodecode>"} เป็น refHn
              </small>

              <small className="text-muted d-block mt-2">
                Map: Hospital→refCustomer, HN หรือ HN {"<nodecode>"}→hn, HN{" "}
                {"<nodecode>"}→refHn, คำนำหน้า→titleTh, ชื่อ→firstnameTh,
                นามสกุล→lastnameTh, DOB→dateOfBirth, Phone No.→mobile, Note +
                หมายเหตุ→note, E-mail→email, Personal ID→nationalId,
                Gender→gender, ที่อยู่ หรือ Address→address1
              </small>

              <small className="text-muted d-block mt-2">
                หากพบข้อมูลไม่ตรงกันหรือข้อมูลไม่ครบ ระบบจะสร้างไฟล์ Excel
                ปัญหาและดาวน์โหลดอัตโนมัติ
              </small>
              <small className="text-muted d-block">
                ไฟล์ปัญหาจะมี 3 ชีท: HN Mismatch, Patient Name Mismatch และ
                Missing Required Data
              </small>

              {pendingFile && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "#fff3cd",
                    border: "1px solid #ffe69c",
                    color: "#664d03",
                    fontWeight: 600,
                    fontSize: 14,
                    display: "inline-block",
                    minWidth: 320,
                  }}
                >
                  Selected file: {pendingFile.name}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Patient Registration Form</h4>
            </div>

            <div className="card-body">
              <div className="form-validation">
                <form
                  className="form-valide"
                  action="#"
                  method="post"
                  onSubmit={submitHandler}
                >
                  <div className="row">
                    <div className="col-xl-6">
                      <PatientTextInputForm
                        title="HN"
                        id="hn"
                        name="hn"
                        refvalue={(el) => (profileRef.current["hn"] = el)}
                        value={hn}
                        onChange={(e) => {
                          setHN(normalizePatientHn(e.target.value));
                        }}
                      />

                      <PatientTextInputNotRequiredForm
                        title="คำนำหน้า"
                        id="prename-th"
                        name="prename-th"
                        refvalue={(el) => (profileRef.current["titleTh"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="ชื่อ"
                        id="first-name-th"
                        name="first-name-th"
                        refvalue={(el) =>
                          (profileRef.current["firstnameTh"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="นามสกุล"
                        id="last-name-th"
                        name="last-name-th"
                        refvalue={(el) =>
                          (profileRef.current["lastnameTh"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="รหัสโรงพยาบาลอ้างอิง"
                        id="reference-hn"
                        name="reference-hn"
                        refvalue={(el) => (profileRef.current["refHn"] = el)}
                        description="*ไม่ต้องใส่ HN ใส่เฉพาะตัวเลข"
                      />

                      <PatientDropDownFormRequired
                        title="Node"
                        htmlFor="node-ref"
                        id="node-ref"
                        name="node-ref"
                        placeholder="กดเพื่อเลือก"
                        options={nodeList.map((node) => node.nodename)}
                        refvalue={(el) => (profileRef.current["nodeRef"] = el)}
                        onChange={async (e) => {
                          await onChangeNode(e.target.value);
                          setCustomerList(CustomerList(e.target.value));
                        }}
                      />

                      <PatientDropDownFormRequired
                        title="Customer"
                        id="customer"
                        placeholder="กดเพื่อเลือก"
                        refvalue={(el) =>
                          (profileRef.current["refCustomer"] = el)
                        }
                        options={customerList}
                      />

                      <PatientTextInputNotRequiredForm
                        title="เลขประจำตัวประชาชน 13 หลัก"
                        id="national-id"
                        name="national-id"
                        refvalue={(el) =>
                          (profileRef.current["nationalId"] = el)
                        }
                      />

                      <PatientDateOfBirthDropdown
                        title="วันเดือนปีเกิด"
                        refvalue={(el) =>
                          (profileRef.current["dateOfBirth"] = el)
                        }
                      />

                      <PatientDropDownForm
                        title="เพศ"
                        htmlFor="gender"
                        id="gender"
                        name="gender"
                        placeholder="กดเพื่อเลือก"
                        options={genders}
                        refvalue={(el) => (profileRef.current["gender"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="เบอร์โทรศัพท์"
                        id="mobile"
                        name="mobile"
                        refvalue={(el) => (profileRef.current["mobile"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="เบอร์โทรศัพท์บ้าน"
                        id="telephone"
                        name="telephone"
                        refvalue={(el) =>
                          (profileRef.current["telephone"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="ประวัติการแพ้ยา"
                        id="medicine-allergic"
                        name="medicine-allergic"
                        refvalue={(el) =>
                          (profileRef.current["medicineIntolerance"] = el)
                        }
                      />

                      <PatientDropDownForm
                        title="สิทธิการเบิก"
                        htmlFor="reimburse-right"
                        id="reimburse-right2"
                        name="reimburse-right2"
                        placeholder="กดเพื่อเลือก"
                        options={paymentPrivilages}
                        refvalue={(el) =>
                          (profileRef.current["healthInsurance"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="สิทธิ์อื่นๆ โปรดระบุ"
                        id="other-reimburse-right"
                        name="other-reimburse-right"
                        refvalue={(el) => (profileRef.current["other"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="คะแนน STOP-BANG"
                        id="stop-bang-score"
                        name="stop-bang-score"
                        refvalue={(el) =>
                          (profileRef.current["stopBangScore"] = el)
                        }
                      />

                      <div className="form-group row">
                        <label
                          className="col-lg-4 col-form-label"
                          style={{ color: "#424242" }}
                        >
                          โรคร่วม
                        </label>

                        <div className="col-lg-6">
                          <input
                            type="hidden"
                            ref={(el) =>
                              (profileRef.current["comorbidity"] = el)
                            }
                          />

                          {[
                            "ความดันโลหิตสูง",
                            "หลอดเลือดสมอง",
                            "หลอดเลือดหัวใจ",
                            "โรคเบาหวาน",
                            "โรคไขมันในเลือดสูง",
                          ].map((option) => (
                            <div className="form-check mb-2" key={option}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`comorbidity-${option}`}
                                value={option}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setComorbidityList((prev) =>
                                    checked
                                      ? prev.concat(option)
                                      : prev.filter((v) => v !== option),
                                  );
                                }}
                              />
                              <label
                                className="form-check-label"
                                style={{ color: "#424242" }}
                                htmlFor={`comorbidity-${option}`}
                              >
                                {option}
                              </label>
                            </div>
                          ))}

                          <div className="form-check mb-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="comorbidity-other"
                              onChange={(e) => {
                                setComorbidityOtherChecked(e.target.checked);
                                if (!e.target.checked) {
                                  setComorbidityOtherText("");
                                }
                              }}
                            />
                            <label
                              className="form-check-label"
                              style={{ color: "#424242" }}
                              htmlFor="comorbidity-other"
                            >
                              อื่นๆ
                            </label>
                          </div>

                          {comorbidityOtherChecked && (
                            <input
                              type="text"
                              className="form-control mt-1"
                              style={{ color: "#424242" }}
                              placeholder="ระบุโรคร่วมอื่นๆ"
                              value={comorbidityOtherText}
                              onChange={(e) =>
                                setComorbidityOtherText(e.target.value)
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-6">
                      <PatientTextInputNotRequiredForm
                        title="คำนำหน้า (อังกฤษ)"
                        id="prename-en"
                        name="prename-en"
                        refvalue={(el) => (profileRef.current["titleEn"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="ชื่อ (อังกฤษ)"
                        id="first-name-en"
                        name="first-name-en"
                        refvalue={(el) =>
                          (profileRef.current["firstnameEn"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="นามสกุล (อังกฤษ)"
                        id="last-name-en"
                        name="last-name-en"
                        refvalue={(el) =>
                          (profileRef.current["lastnameEn"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="อีเมล์"
                        id="email"
                        name="email"
                        refvalue={(el) => (profileRef.current["email"] = el)}
                      />

                      <PatientDropDownForm
                        title="สถานภาพ"
                        htmlFor="married-status"
                        id="married-status"
                        name="married-status"
                        placeholder="กดเพื่อเลือก"
                        options={marriedStatus}
                        refvalue={(el) => (profileRef.current["status"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="เชื้อชาติ"
                        id="ethnicity"
                        name="ethnicity"
                        refvalue={(el) =>
                          (profileRef.current["ethnicity"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="สัญชาติ"
                        id="nationality"
                        name="nationality"
                        refvalue={(el) =>
                          (profileRef.current["nationality"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="ศาสนา"
                        id="religion"
                        name="religion"
                        refvalue={(el) => (profileRef.current["religion"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="หมู่เลือด"
                        id="blood-type"
                        name="blood-type"
                        refvalue={(el) =>
                          (profileRef.current["bloodType"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="อาชีพ"
                        id="occupation"
                        name="occupation"
                        refvalue={(el) =>
                          (profileRef.current["occupation"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="ที่อยู่"
                        id="address1"
                        name="address1"
                        refvalue={(el) => (profileRef.current["address1"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="ที่อยู่ (ต่อ)"
                        id="address2"
                        name="address2"
                        refvalue={(el) => (profileRef.current["address2"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="จังหวัด"
                        id="province"
                        name="province"
                        refvalue={(el) => (profileRef.current["province"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="รหัสไปรษณีย์"
                        id="postal-code"
                        name="postal-code"
                        refvalue={(el) => (profileRef.current["postcode"] = el)}
                      />

                      <PatientTextInputNotRequiredForm
                        title="ชื่อผู้ติดต่ออื่น"
                        id="other-contact"
                        name="other-contact"
                        refvalue={(el) =>
                          (profileRef.current["emergencyContact"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="ความสัมพันธ์"
                        id="other-relation"
                        name="other-relation"
                        refvalue={(el) =>
                          (profileRef.current["relationship"] = el)
                        }
                      />

                      <PatientTextInputNotRequiredForm
                        title="เบอร์ติดต่อผู้ติดต่ออื่น"
                        id="other-phone"
                        name="other-phone"
                        refvalue={(el) =>
                          (profileRef.current["emergencyContactPhone"] = el)
                        }
                      />

                      <div className="form-group row">
                        <label
                          className="col-lg-4 col-form-label"
                          style={{ color: "#424242" }}
                          htmlFor="val-suggestions"
                        >
                          ข้อความ (Remarks)
                        </label>

                        <div className="col-lg-6">
                          <textarea
                            className="form-control"
                            style={{ color: "#424242" }}
                            id="val-suggestions"
                            name="val-suggestions"
                            rows="5"
                            placeholder="เรื่องอื่นๆ เกี่ยวกับคนไข้"
                            ref={(el) => (profileRef.current["note"] = el)}
                          ></textarea>
                        </div>
                      </div>

                      <br />

                      <div className="form-group row">
                        <div className="col-lg-8 ml-auto">
                          <button type="submit" className="btn btn-primary">
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PatientRegister;
